// /calendar/ — the publishing calendar. Reads the JSON the nightly job
// (ops/calendar/build.mjs) writes into the page, then draws a month grid
// and a list. Post titles only ever go in through textContent.
//
// Item fields: t title, d date (ISO, UTC), s published|scheduled|sent,
// k essay|podcast, e emailed, u view or preview link, a Ghost editor link,
// i feature image (scheduled posts only).

const TZ = "Australia/Sydney";
const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
    "August", "September", "October", "November", "December"];
const DOWS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MAIL = '<svg class="gg-cal__mail" viewBox="0 0 24 24" role="img" aria-label="Post and email"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/></svg>';

// Sydney date parts for a UTC instant.
const partsFmt = new Intl.DateTimeFormat("en-AU", { timeZone: TZ, year: "numeric", month: "numeric", day: "numeric", weekday: "short", hour: "numeric", minute: "2-digit", hour12: true });
function sydney(iso) {
    const p = Object.fromEntries(partsFmt.formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
    return { y: +p.year, m: +p.month - 1, d: +p.day, dow: p.weekday, time: `${p.hour}:${p.minute}${p.dayPeriod.toLowerCase().replace(/\./g, "")}` };
}
const key = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

function el(tag, cls, text) {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
}

export default function calendar() {
    const root = document.querySelector("[data-calendar]");
    if (!root) return;
    const $ = (s) => root.querySelector(s);
    const status = $("[data-cal-status]");
    const source = document.getElementById("gg-calendar-data");
    let data;
    try { data = JSON.parse(source.textContent); } catch {
        status.textContent = "The calendar data isn't there yet. It fills in overnight, or Shawn can run it by hand.";
        return;
    }

    const now = new Date();
    const items = data.items.map((x) => {
        const z = sydney(x.d);
        // A scheduled post whose time has passed went out after the last refresh.
        const state = x.s === "scheduled" && new Date(x.d) <= now ? "published" : x.s;
        return { ...x, z, state, day: key(z.y, z.m, z.d), label: x.k === "podcast" ? "Podcast" : x.s === "sent" ? "Email only" : "Essay" };
    }).sort((a, b) => a.d.localeCompare(b.d));
    const byDay = items.reduce((m, x) => ((m[x.day] = m[x.day] || []).push(x), m), {});
    const today = sydney(now.toISOString());
    const todayKey = key(today.y, today.m, today.d);
    const upcoming = items.filter((x) => x.state === "scheduled");
    const phone = window.matchMedia("(max-width: 760px)");

    let view = { y: today.y, m: today.m };
    let mode = phone.matches ? "list" : "month";
    let picked = upcoming[0] || items[items.length - 1];

    const when = (x) => `${x.z.dow} ${x.z.d} ${MONTHS[x.z.m]} ${x.z.y}${x.state === "scheduled" ? `, ${x.z.time}` : ""}`;
    const kind = (x) => (x.s === "sent" ? "Email only" : x.e ? "Post + email to The Letter" : "Post only");

    // At a glance
    const glance = $("[data-cal-glance]");
    const lastPod = [...items].reverse().find((x) => x.k === "podcast" && x.state !== "scheduled");
    const nextPod = upcoming.find((x) => x.k === "podcast");
    const nextMail = upcoming.filter((x) => x.e);
    const cards = [
        ["Next up", upcoming[0] ? upcoming[0].t : "Nothing scheduled", upcoming[0] ? `${when(upcoming[0])} · ${upcoming[0].e ? "Post + email" : "Post only"}` : "Schedule a post in Ghost"],
        ["Scheduled", `${upcoming.length} post${upcoming.length === 1 ? "" : "s"}`, upcoming.length ? `Through to ${when(upcoming[upcoming.length - 1]).replace(/,.*$/, "")}` : ""],
        ["Next newsletter", nextMail[0] ? `${nextMail[0].z.dow} ${nextMail[0].z.d} ${MONTHS[nextMail[0].z.m].slice(0, 3)}` : "None scheduled", nextMail[0] ? `${nextMail[0].t} · ${nextMail.length} to go` : ""],
        [nextPod ? "Next episode" : "Latest episode", (nextPod || lastPod) ? (nextPod || lastPod).t : "None yet", nextPod ? when(nextPod) : lastPod ? `${when(lastPod)} · none scheduled` : ""],
    ];
    for (const [k, big, small] of cards) {
        const c = el("div", "gg-cal__card");
        c.append(el("p", "gg-cal__card-k", k), el("p", "gg-cal__card-big", big), el("p", "gg-cal__card-small", small));
        glance.append(c);
    }

    function chip(x) {
        const b = el("button", `gg-cal__item gg-cal__item--${x.k} gg-cal__item--${x.state}${x === picked ? " is-picked" : ""}`);
        b.type = "button";
        const top = el("span", "gg-cal__item-top");
        top.append(el("span", "gg-cal__dot", ""), el("span", null, x.state === "scheduled" ? x.z.time : x.s === "sent" ? "Email" : "Published"));
        if (x.e) top.insertAdjacentHTML("beforeend", MAIL);
        b.append(top, el("span", "gg-cal__item-title", x.t));
        b.addEventListener("click", () => {
            picked = x;
            render();
            if (phone.matches) $("[data-cal-detail]").scrollIntoView({ behavior: "smooth", block: "start" });
        });
        return b;
    }

    function renderGrid() {
        const grid = $("[data-cal-grid]");
        grid.replaceChildren();
        for (const d of DOWS) grid.append(el("div", "gg-cal__dow", d));
        const first = new Date(Date.UTC(view.y, view.m, 1));
        const offset = (first.getUTCDay() + 6) % 7;
        for (let i = 0; i < 42; i++) {
            const dt = new Date(Date.UTC(view.y, view.m, 1 - offset + i));
            const k = key(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate());
            const cell = el("div", `gg-cal__cell${dt.getUTCMonth() !== view.m ? " is-out" : ""}${k === todayKey ? " is-today" : ""}`);
            const head = el("div", "gg-cal__cell-head");
            head.append(el("span", "gg-cal__num", String(dt.getUTCDate())));
            if (k === todayKey) head.append(el("span", "gg-cal__today", "Today"));
            cell.append(head);
            for (const x of byDay[k] || []) cell.append(chip(x));
            grid.append(cell);
        }
    }

    function renderList() {
        const list = $("[data-cal-list]");
        list.replaceChildren();
        const inMonth = items.filter((x) => x.z.y === view.y && x.z.m === view.m);
        if (!inMonth.length) list.append(el("li", "gg-cal__empty", "Nothing this month."));
        for (const x of inMonth) {
            const li = el("li", "gg-cal__row");
            const b = chip(x);
            b.prepend(el("span", "gg-cal__row-day", `${x.z.dow} ${x.z.d}`));
            li.append(b);
            list.append(li);
        }
    }

    function renderDetail() {
        const box = $("[data-cal-detail]");
        box.replaceChildren();
        if (!picked) return;
        const x = picked;
        const k = el("p", `gg-cal__detail-k gg-cal__detail-k--${x.k}`);
        k.append(el("span", "gg-cal__dot", ""), document.createTextNode(`${x.label} · ${x.state === "scheduled" ? "Scheduled" : x.s === "sent" ? "Sent" : "Published"}`));
        box.append(k, el("p", "gg-cal__detail-title", x.t), el("p", "gg-cal__detail-meta", when(x) + (x.state === "scheduled" ? " Sydney" : "")), el("p", "gg-cal__detail-meta", kind(x)));
        if (x.i) {
            const img = el("img", "gg-cal__detail-img");
            img.src = x.i; img.alt = ""; img.loading = "lazy";
            box.append(img);
        }
        const acts = el("div", "gg-cal__detail-actions");
        if (x.u) {
            const a = el("a", "gg-cal__cta", x.state === "scheduled" ? "Read the preview" : "View on the site");
            a.href = x.u; a.target = "_blank"; a.rel = "noopener";
            acts.append(a);
        }
        const g = el("a", "gg-cal__cta gg-cal__cta--ghost", x.state === "scheduled" ? "Change date in Ghost" : "Open in Ghost");
        g.href = x.a; g.target = "_blank"; g.rel = "noopener";
        acts.append(g);
        box.append(acts);
    }

    function renderUpcoming() {
        const ol = $("[data-cal-upcoming]");
        ol.replaceChildren();
        for (const x of upcoming.slice(0, 6)) {
            const li = el("li", "gg-cal__up");
            const b = el("button", "gg-cal__up-btn");
            b.type = "button";
            b.append(el("span", "gg-cal__up-date", `${x.z.dow} ${x.z.d} ${MONTHS[x.z.m].slice(0, 3)}`), el("span", "gg-cal__up-title", x.t));
            if (x.e) b.insertAdjacentHTML("beforeend", MAIL);
            b.addEventListener("click", () => { picked = x; view = { y: x.z.y, m: x.z.m }; render(); });
            li.append(b);
            ol.append(li);
        }
    }

    function render() {
        $("[data-cal-month]").textContent = `${MONTHS[view.m]} ${view.y}`;
        for (const b of root.querySelectorAll("[data-cal-view]")) b.setAttribute("aria-pressed", String(b.dataset.calView === mode));
        $("[data-cal-grid]").hidden = mode !== "month";
        $("[data-cal-list]").hidden = mode !== "list";
        if (mode === "month") renderGrid(); else renderList();
        renderDetail();
    }

    const step = (n) => { const m = view.m + n; view = { y: view.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 }; render(); };
    $("[data-cal-prev]").addEventListener("click", () => step(-1));
    $("[data-cal-next]").addEventListener("click", () => step(1));
    $("[data-cal-today]").addEventListener("click", () => { view = { y: today.y, m: today.m }; render(); });
    for (const b of root.querySelectorAll("[data-cal-view]")) b.addEventListener("click", () => { mode = b.dataset.calView; render(); });

    $("[data-cal-updated]").textContent = `Last updated ${new Date(data.updated).toLocaleString("en-AU", { timeZone: TZ, weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}`;
    status.hidden = true;
    glance.hidden = false;
    $("[data-cal-body]").hidden = false;
    renderUpcoming();
    render();
}
